import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import "./HallFameContent.css";
import { hallfameAssets } from "../../imagesData/ImagesData";
import useFetchImageUrls from "../../ConstantUseFetchImageUrls";
// import HallFameData from "./HallFameData.json";
// import HallFamePerformersData from "./HallFamePerformersData.json";
import MonthlyLeadersTab from "./MonthlyLeadersTab";
import { BASE_API_URL } from "utils/constants";
import PersonIcon from "@mui/icons-material/Person";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import CircularProgress from "@mui/material/CircularProgress";
import { getUserData } from "Redux/UserDetails";

function HallFameContent() {
  const query = useMemo(
    () => `params=${encodeURIComponent(JSON.stringify(hallfameAssets))}`,
    [],
  );
  const { imageUrls, error } = useFetchImageUrls(query);

  const urls = imageUrls?.imageUrl || {};

  const performanceConfig = {
    totalCoursesCompleted: {
      heading: "Total Trainings Completed",
      image: "/Assets/HallFame/performance-icon1.png",
    },
    totalBadgesEarned: {
      heading: "Badges Earned",
      image: "/Assets/HallFame/performance-icon2.png",
    },
    totalHours: {
      heading: "Total Hours",
      image: "/Assets/HallFame/performance-icon3.png",
    },
    completionPercentage: {
      heading: "Completion Status",
      image: "/Assets/HallFame/performance-icon2.png",
    },
  };

  const getExtrasByRank = (index) => {
    const map = [
      {
        medal: "/Assets/HallFame/platinum-medal.png",
        ranking: "/Assets/HallFame/rankimg2.png",
      },
      {
        medal: "/Assets/HallFame/diamond-medal.png",
        ranking: "/Assets/HallFame/rankimg1.png",
      },
      {
        medal: "/Assets/HallFame/gold-medal.png",
        ranking: "/Assets/HallFame/rankimg3.png",
      },
    ];

    return map[index] ?? { medal: null, ranking: null };
  };

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [hallFameData, setHallFameData] = useState([]);
  const [hallFamePerformersData, setHallFamePerformersData] = useState([]);
  const [enterpriseIds, setEnterpriseIds] = useState([]);
  const [top3MUs, setTop3MUs] = useState([]);
  const userData = useSelector((state) => state.userDetails.userData?.[0]);

  const { instance } = useMsal();
  const dispatch = useDispatch();
  const activeAccount = instance.getActiveAccount();
  const enterpriseId = activeAccount?.idTokenClaims?.enterpriseID;

  const safeFetchJson = async (url, options) => {
    const res = await fetch(url, options);

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Request failed: ${res.status} ${res.statusText} ${text ? `- ${text}` : ""}`,
      );
    }

    return res.json();
  };

  useEffect(() => {
    if (!enterpriseId) return;

    dispatch(getUserData(enterpriseId));
  }, [enterpriseId, dispatch]);

  const businessUnit = useMemo(
    () => userData?.businessUnit || "Song",
    [userData?.businessUnit],
  );

  useEffect(() => {
    if (!enterpriseId || !businessUnit) return;

    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const token = localStorage.getItem("token");
        if (!token) return;

        const headers = {
          Authorization: token,
          "Content-Type": "application/json",
        };

        const bu = encodeURIComponent(businessUnit);

        const [hallResponse, performerDataRaw] = await Promise.all([
          safeFetchJson(
            `${BASE_API_URL}/overallPerformance?businessUnit=${bu}`,
            { headers },
          ),
          safeFetchJson(`${BASE_API_URL}/getTopLearners?businessUnit=${bu}`, {
            headers,
          }),
        ]);

        if (!isMounted) return;

        const hallData = Array.isArray(hallResponse)
          ? typeof hallResponse[0] === "object"
            ? hallResponse[0]
            : {}
          : typeof hallResponse === "object"
            ? hallResponse
            : {};

        const formattedData = Object.entries(hallData)
          .filter(([key]) => performanceConfig[key])
          .map(([key, value]) => ({
            id: key,
            heading: performanceConfig[key].heading,
            number:
              key === "completionPercentage" ? `${value ?? 0}%` : (value ?? 0),
            image: performanceConfig[key].image,
          }));

        setHallFameData(formattedData);

        const cleanData = (
          Array.isArray(performerDataRaw) ? performerDataRaw : []
        )
          .filter((x) => x?.enterpriseId)
          .reduce((acc, curr) => {
            if (!acc.some((x) => x.enterpriseId === curr.enterpriseId)) {
              acc.push({
                ...curr,
                totalPoints: Number(curr.totalPoints || 0),
              });
            }
            return acc;
          }, []);

        const top3 = cleanData
          .sort((a, b) => b.totalPoints - a.totalPoints)
          .slice(0, 3);

        const reordered = [top3[1], top3[0], top3[2]]
          .filter(Boolean)
          .map((item, index) => {
            const extras = getExtrasByRank(index);
            return {
              ...item,
              medal: extras.medal ?? "/Assets/HallFame/gold-medal.png",
              ranking: extras.ranking ?? "/Assets/HallFame/rankimg3.png",
            };
          });

        if (!isMounted) return;

        setHallFamePerformersData(reordered);

        const entIds = reordered.map((x) => x?.enterpriseId).filter(Boolean);

        setEnterpriseIds(entIds);
      } catch (err) {
        if (!isMounted) return;

        console.error(err);

        setLoadError(err?.message || "Failed to load data");
        setHallFameData([]);
        setHallFamePerformersData([]);
        setEnterpriseIds([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [enterpriseId, businessUnit]);

  if (error) return <div>Error loading assets.</div>;
  if (!imageUrls || Object.keys(imageUrls).length === 0) return null;

  return (
    <>
      <div className="hallfame-main-container">
        {/* first section */}
        <div className="hallfame-overall-section">
          <div className="hallfame-overall-wrap">
            {/* <h2 className="section-headings">Overall Ignite performance</h2>
            <p className="last-update-text">Last updated - 4th Jan</p>
            <div className="hallfame-overall-row row1">
              <div className="hallfame-overall-box">
                <div className="hallfame-performance-wrap">
                  {hallFameData.map((item, index) => {
                    return (
                      <div key={item.id} className="hallfame-performance-row">
                        <div className="hallfame-performance-box">
                          <div className="hallfame-performance-content">
                            <h3>{item.heading}</h3>
                            <h4>{item.number}</h4>
                          </div>
                          <img
                            src={item.image}
                            alt={item.heading}
                            className="performance-icon"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div> */}
            <div className="hallfame-overall-row row2">
              <div className="hallfame-overall-box">
                <div className="hallfame-performers-wrap">
                  <h3>🏆 Top Hall of fame Performers 🏆</h3>
                  <p className="champ-learn">The Champions of Learning</p>
                  <div className="hallfame-performers-extra-row">
                    {loading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                          padding: "24px",
                        }}
                      >
                        <CircularProgress sx={{ color: "#57008a" }} />
                      </div>
                    ) : loadError ? (
                      <p style={{ margin: 0, color: "#ffffffb0" }}>
                        {loadError}
                      </p>
                    ) : hallFamePerformersData.length === 0 ? (
                      <div
                        style={{
                          width: "100%",
                          textAlign: "center",
                          padding: "24px",
                          color: "#ffffffb0",
                          fontSize: "1rem",
                        }}
                      >
                        No learners' data recorded yet
                      </div>
                    ) : (
                      hallFamePerformersData.map((item, index) => {
                        const MUs = top3MUs.find(
                          (x) => x.enterpriseId === item.enterpriseId,
                        )?.muSmart;

                        return (
                          <div
                            key={item.enterpriseId}
                            className="hallfame-performers-row"
                          >
                            <div className="hallfame-performers-box">
                              {item.photo ? (
                                <img
                                  src={item.photo}
                                  alt={item.name}
                                  className="performer-img"
                                />
                              ) : (
                                <PersonIcon
                                  className="performer-img"
                                  sx={{ fontSize: "4rem" }}
                                />
                              )}

                              <img
                                src={item.medal}
                                alt="medal"
                                className="performer-medal"
                              />

                              <h4>{item.enterpriseId}</h4>

                              <p className="total-points-section">
                                <span>
                                  <CropSquareIcon
                                    sx={{
                                      transform: "rotate(45deg)",
                                      marginRight: "3px",
                                      fontSize: "0.80rem",
                                      color: "#FEA410",
                                    }}
                                  />
                                </span>
                                <span
                                  style={{
                                    color: "#FEA410",
                                    fontWeight: "bolder",
                                    fontSize: "0.95rem",
                                  }}
                                >
                                  {Number(item.totalPoints)}
                                </span>
                              </p>

                              <p>
                                <span>{MUs ?? "-"}</span>
                              </p>

                              <div className="ranking-holder">
                                <img
                                  src={item.ranking}
                                  alt="rank"
                                  className="performer-rank-img"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* second section */}
        <div className="monthly-leaders-section">
          <div className="monthly-leaders-wrap">
            <MonthlyLeadersTab
              enterpriseIds={enterpriseIds}
              onTop3MUs={setTop3MUs}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default HallFameContent;
